/**
 * Analyze tab — Structural analysis execution tools.
 *
 * Run Structural Analysis now drives the full pipeline (rebuild analytical
 * model → ensure standard load combinations → solve → store results for the
 * color-fill overlay) instead of only logging to console. Results & Color
 * Fill toggles the member-force diagram in the viewport.
 */

import { BaseTool } from '../BaseTool';
import {
    analyticalModelManager,
    structuralLoadManager,
    feaSolver,
    rebuildAnalyticalModel,
} from '../../core/services';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import { AnalysisResults } from '../../core/FEASolver';
import i18n from '../../i18n';

export class RunStructuralAnalysisTool extends BaseTool {
    id = 'ANALYZE_RUN_ANALYSIS';
    name = 'Run Structural Analysis';
    cursor = 'default';

    onActivate(): void {
        const store = useAnalysisStore.getState();
        store.setStructuralAnalysisRunning(true);

        // 1. Analytical model must exist and be current.
        rebuildAnalyticalModel();
        if (analyticalModelManager.getMembers().length === 0) {
            this.fail(i18n.t('tools.notify.noAnalyticalMembersExistPlaceStructural'));
            return;
        }
        if (!analyticalModelManager.isModelValid()) {
            const errors = analyticalModelManager.getErrorMessages();
            this.fail(i18n.t('tools.notify.analyticalIssues', { count: errors.length, first: errors[0] ?? '' }));
            return;
        }

        // 2. Any supported node? Without one the stiffness matrix is singular.
        const hasSupport = analyticalModelManager.getNodes().some(n => n.boundaryCondition !== 'Free');
        if (!hasSupport) {
            this.fail(i18n.t('tools.notify.noBoundaryConditionsDefinedUseBoundary'));
            return;
        }

        // 3. Ensure LRFD combinations exist so a solvable load case is present.
        let combination = structuralLoadManager.getAllCombinations()[0];
        if (!combination) {
            structuralLoadManager.generateStandardCombinations('LRFD');
            combination = structuralLoadManager.getAllCombinations()[0];
        }
        if (!combination) {
            this.fail(i18n.t('tools.notify.noLoadCombinationsAvailableDefineThem'));
            return;
        }

        // 4. Solve.
        const results: AnalysisResults = feaSolver.analyze(combination.id);
        if (!results.isConverged) {
            this.fail(i18n.t('tools.notify.analysisDidNotConvergeCheckSupports'));
            return;
        }

        useAnalysisStore.getState().setLastFeaResults(results);
        useAnalysisStore.getState().setLastStructuralRun({
            combinationName: combination.name,
            maxDisplacement: results.maxDisplacement,
            maxStress: results.maxStress,
            converged: true,
            message: `Solved ${analyticalModelManager.getMembers().length} member(s) in ${results.analysisTime.toFixed(0)} ms.`,
        });
        useAnalysisStore.getState().setReport({
            title: `Structural Analysis — ${combination.name}`,
            generatedOn: Date.now(),
            lines: [
                `Combination: ${combination.name}`,
                `Nodes: ${analyticalModelManager.getNodes().length} · Members: ${analyticalModelManager.getMembers().length}`,
                `Max displacement: ${(results.maxDisplacement * 1000).toFixed(2)} mm`,
                `Max stress: ${(results.maxStress / 1e6).toFixed(1)} MPa`,
                `Solve time: ${results.analysisTime.toFixed(1)} ms`,
                ``,
                `Support reactions (kN):`,
                ...Array.from(results.reactions.values())
                    .filter(r => Math.abs(r.fz) > 0.001)
                    .slice(0, 20)
                    .map((r, i) => `  support ${i + 1}: Fz ${r.fz.toFixed(1)} · Fx ${r.fx.toFixed(1)} · Fy ${r.fy.toFixed(1)}`),
            ],
        });
        useAnalysisStore.getState().setShowResults(true);
        store.setStructuralAnalysisRunning(false);
        this.notify(i18n.t('tools.notify.analysisComplete', { name: combination.name }), 'success');
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    private fail(message: string): void {
        useAnalysisStore.getState().setStructuralAnalysisRunning(false);
        this.notify(message, 'error');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class ShowResultsTool extends BaseTool {
    id = 'ANALYZE_SHOW_RESULTS';
    name = 'Results & Color Fill';
    cursor = 'default';

    onActivate(): void {
        const store = useAnalysisStore.getState();
        if (!store.lastFeaResults) {
            this.notify(i18n.t('tools.notify.runAStructuralAnalysisFirstTo'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        store.toggleResults();
        this.notify(i18n.t('tools.notify.resultsOverlayToggled', { state: store.showResults ? i18n.t('tools.notify.stateDisabled') : i18n.t('tools.notify.stateEnabled') }), 'info');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
